import { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import useCourtSlotAvailability from "../../../pages/canchas/components/useCourtSlotAvailability";
import CourtSlotAvailabilityPicker from "../../../pages/canchas/components/CourtSlotAvailabilityPicker";
import OrganizerTimePillSelect from "../../components/OrganizerTimePillSelect";
import {
	SingleDatePickerPill,
	COUNTRIES,
} from "../../../pages/reservas/components/ReservaShared";
import PhoneCountryInput from "../../../pages/reservas/components/PhoneCountryInput";
import { Campo } from "../../reservas/primerUso/barPasos";
import TurnoFijoBlock from "../../turnos/agenda/TurnoFijoBlock";
import useSerieFija from "../../turnos/agenda/useSerieFija";
import { duracionLabel, plata } from "../../turnos/agenda/agendaLayout";
import {
	organizerCreateReservationApi,
	organizerPreviewReservationSeriesApi,
	organizerCreateReservationSeriesApi,
} from "../../../api/api";
import { pluralTurnos } from "./courtSeriesModel";
import "../../events/EventForm.css";
import "../../turnos/turnos-beauty.css";
import "../../primerUso/primerUso.css";
import "../reservas-organizer.css";
import "../../turnos/agenda/agenda.css";
import "./NewInternalReservationModal.css";
import "./CourtSeries.css";

/*
 * Organizer-side counterpart to QuickSlotModal (buyer flow, design.md V3).
 * Reuses useCourtSlotAvailability + CourtSlotAvailabilityPicker for
 * date/court/slot selection. No auth gate (the organizer route is already
 * staff-authenticated), no hold call, no checkout navigation: submit calls
 * organizerCreateReservationApi directly and the reservation is created
 * already `confirmed` server-side (spec V2/R1). A 409 conflict shows a
 * distinct message and reloads availability (spec R12), mirroring
 * QuickSlotModal's own 409 handling.
 *
 * Layout and classes are those of Beauty's NuevaReservaModal (side panel, ef-label
 * fields, footer Cancelar / Crear reserva); the court, duration and price picker is
 * the courts' own.
 *
 * "Turno fijo": the same court, weekday and time every 1, 2, 3 or 4 weeks until a
 * required end date (the shared TurnoFijoBlock, without a professional).
 * "Ver fechas" asks the preview endpoint for the state of every date;
 * the confirm button then reads "Crear N turnos" and calls the series endpoint,
 * which books the available dates and reports the rest. A 409 (no date
 * available) keeps the form and shows the skipped dates. With the toggle off
 * nothing here changes the single-reservation flow.
 */
export default function NewInternalReservationModal({
	show,
	bar,
	onHide,
	onCreated,
	onSeriesCreated,
	onConflict,
	defaultDate,
	defaultCourtId,
	defaultSlotStart,
}) {
	const clubId = bar?._id || null;
	const initialDate = useCallback(
		() => defaultDate || dayjs().format("YYYY-MM-DD"),
		[defaultDate],
	);

	// Organizer may create a manual reservation for today or any future date
	// within the club's normal booking window (mirrors the buyer flow's
	// minDate/maxDate wiring in useCourtReservationFlow.js / ClubDetailDesktop),
	// never a past date.
	const minDate = useMemo(() => dayjs().startOf("day"), []);
	const windowDays = Number(bar?.reservationWindowDays || 14);
	const maxDate = useMemo(
		() => minDate.add(windowDays, "day"),
		[minDate, windowDays],
	);

	const [dateLabel, setDateLabel] = useState(initialDate);
	const [slotStart, setSlotStart] = useState(defaultSlotStart || "");
	const [name, setName] = useState("");
	const [phoneCountryCode, setPhoneCountryCode] = useState("AR");
	const [phoneNumber, setPhoneNumber] = useState("");
	const [phoneValid, setPhoneValid] = useState(false);
	const [phoneTouched, setPhoneTouched] = useState(false);
	const [notes, setNotes] = useState("");
	const [submitting, setSubmitting] = useState(false);
	const [submitError, setSubmitError] = useState("");
	const [conflictRefreshBlocked, setConflictRefreshBlocked] = useState(false);
	const slot = useMemo(() => (slotStart ? { slotStart } : null), [slotStart]);

	const {
		loading,
		loadError,
		activeDuration,
		setActiveDuration,
		activeSport,
		setActiveSport,
		selectedCourtId,
		setSelectedCourtId,
		courtOptions,
		selectedOption,
		durations,
		sports,
		reload: loadAvailability,
	} = useCourtSlotAvailability({
		clubId,
		dateLabel,
		slot,
		selectedSportFilter: undefined,
		club: bar,
		show,
		// The organizer books turns that are not being sold, so an unpriced
		// duration is still a valid choice here.
		onlyPricedDurations: false,
	});

	// Turno fijo: the preview is only valid for the exact court/date/time/duration/
	// interval/end it was asked for, so any change makes it stale (the key no longer
	// matches) and the organizer has to look at the dates again.
	const turnoFijo = useSerieFija({
		date: dateLabel,
		listo: Boolean(selectedOption),
		claveParts: [
			selectedOption?.court.courtId,
			selectedOption?.entry.slotStart,
			activeDuration,
		],
		pedirVistaPrevia: async ({ intervalWeeks, dateTo }) => {
			const { court, entry } = selectedOption;
			const response = await organizerPreviewReservationSeriesApi({
				clubId,
				courtId: court.courtId,
				dateFrom: dateLabel,
				dateTo,
				slotStart: entry.slotStart,
				durationMinutes: activeDuration,
				intervalWeeks,
			});
			const dates = response?.data?.info?.dates;
			return { dates: Array.isArray(dates) ? dates : [] };
		},
		mensajeDeError: (err) =>
			err?.response?.data?.message ||
			"No pudimos revisar las fechas. Intentá de nuevo.",
	});
	const fixed = turnoFijo.fixed;
	const { reiniciar: reiniciarTurnoFijo } = turnoFijo;

	useEffect(() => {
		if (!show) return;
		setDateLabel(initialDate());
		setSlotStart(defaultSlotStart || "");
		setSelectedCourtId(defaultCourtId || null);
		setSubmitError("");
		setConflictRefreshBlocked(false);
		reiniciarTurnoFijo();
	}, [
		show,
		initialDate,
		defaultCourtId,
		defaultSlotStart,
		setSelectedCourtId,
		reiniciarTurnoFijo,
	]);

	useEffect(() => {
		if (
			show &&
			defaultCourtId &&
			courtOptions.some(({ court }) => court.courtId === defaultCourtId)
		) {
			setSelectedCourtId(defaultCourtId);
		}
	}, [show, defaultCourtId, courtOptions, setSelectedCourtId]);

	const resetForm = () => {
		setDateLabel(initialDate());
		setSlotStart(defaultSlotStart || "");
		setName("");
		setPhoneCountryCode("AR");
		setPhoneNumber("");
		setPhoneValid(false);
		setPhoneTouched(false);
		setNotes("");
		setSubmitError("");
		setConflictRefreshBlocked(false);
		reiniciarTurnoFijo();
	};

	const handleHide = () => {
		resetForm();
		onHide?.();
	};

	const { availableCount, rangeError: seriesRangeError } = turnoFijo;

	const canSubmit =
		Boolean(selectedOption) &&
		name.trim().length > 0 &&
		phoneValid &&
		!submitting &&
		!conflictRefreshBlocked &&
		(!fixed || (availableCount > 0 && !seriesRangeError));

	const refreshAfterConflict = async () => {
		try {
			await onConflict?.();
			await loadAvailability();
			setConflictRefreshBlocked(false);
			return true;
		} catch {
			setConflictRefreshBlocked(true);
			return false;
		}
	};

	const selectedCountry = useMemo(
		() => COUNTRIES.find((c) => c.code === phoneCountryCode) || COUNTRIES[0],
		[phoneCountryCode],
	);

	const handleSubmit = async () => {
		if (!selectedOption) return;
		const trimmedName = name.trim();
		const trimmedPhoneNumber = phoneNumber.trim();
		if (
			!trimmedName ||
			!trimmedPhoneNumber ||
			!selectedCountry.validate(trimmedPhoneNumber)
		) {
			setPhoneTouched(true);
			setSubmitError("Nombre y teléfono son obligatorios.");
			return;
		}

		const { court, entry } = selectedOption;
		// Store phone with its dial code so the organizer panel's WhatsApp
		// "Comunicarse" action (OrgReservationsScreen) can build a wa.me link
		// by stripping non-digits, mirroring the buyer flow's stored format
		// (see useCourtReservationFlow.js proceedWithReservation).
		const fullPhone = `${selectedCountry.prefix} ${trimmedPhoneNumber}`;

		if (fixed) {
			if (!turnoFijo.current || availableCount === 0 || seriesRangeError) return;
			try {
				setSubmitting(true);
				setSubmitError("");
				turnoFijo.setSkipped(null);
				const response = await organizerCreateReservationSeriesApi({
					clubId,
					courtId: court.courtId,
					dateFrom: dateLabel,
					intervalWeeks: turnoFijo.intervalWeeks,
					dateTo: turnoFijo.dateTo,
					slotStart: entry.slotStart,
					durationMinutes: activeDuration,
					name: trimmedName,
					phone: fullPhone,
					notes: notes.trim() || undefined,
				});
				await onSeriesCreated?.(response?.data?.info || null);
				resetForm();
				onHide?.();
			} catch (err) {
				const data = err?.response?.data;
				// 409: none of the dates could be booked. The form stays so the
				// organizer can change the court, time or end date.
				if (err?.response?.status === 409) {
					setSubmitError(data?.message || "Ninguna de las fechas está disponible.");
					turnoFijo.setSkipped(
						(Array.isArray(data?.skipped) ? data.skipped : []).map((entry) => ({
							...entry,
							available: false,
						})),
					);
				} else {
					setSubmitError(
						data?.message || "No pudimos crear el turno fijo. Intentá de nuevo.",
					);
				}
			} finally {
				setSubmitting(false);
			}
			return;
		}

		try {
			setSubmitting(true);
			setSubmitError("");
			const response = await organizerCreateReservationApi({
				clubId,
				courtId: court.courtId,
				date: dateLabel,
				slotStart: entry.slotStart,
				durationMinutes: activeDuration,
				name: trimmedName,
				phone: fullPhone,
				notes: notes.trim() || undefined,
			});
			const reservation = response?.data?.info?.reservation || null;
			await onCreated?.(reservation);
			resetForm();
			onHide?.();
		} catch (err) {
			const status = err?.response?.status;
			if (status === 409) {
				setSubmitError(
					err?.response?.data?.message ||
						"Ese horario ya fue reservado. Elegí otro turno.",
				);
				setSelectedCourtId(null);
				setConflictRefreshBlocked(true);
				await refreshAfterConflict();
				return;
			}
			setSubmitError(
				err?.response?.data?.message ||
					"No pudimos crear la reserva. Intentá de nuevo.",
			);
		} finally {
			setSubmitting(false);
		}
	};

	if (!show) return null;

	const price = Number(selectedOption?.entry?.price) || 0;

	return (
		<div className="zr-overlay" onMouseDown={submitting ? undefined : handleHide}>
			<div
				className="zr-modal zr-side-panel zr-srv-modal ba-modal nir-panel"
				role="dialog"
				aria-modal="true"
				aria-label="Nueva reserva"
				onMouseDown={(e) => e.stopPropagation()}
			>
				<div className="zr-side-panel__head">
					<h3 className="zr-side-panel__title">
						Nueva reserva{bar?.name ? ` | ${bar.name}` : ""}
					</h3>
					<button
						type="button"
						className="zr-btn zr-btn--ghost zr-side-panel__close"
						onClick={handleHide}
						aria-label="Cerrar"
						disabled={submitting}
					>
						×
					</button>
				</div>
				<div className="pu-body">
					{(submitError || conflictRefreshBlocked) && (
						<div className="ef-alert" role="alert">
							{submitError ||
								"No pudimos actualizar la agenda autoritativa. Reintentá antes de continuar."}
							{conflictRefreshBlocked && (
								<div className="mt-2">
									<button
										type="button"
										className="zr-btn zr-btn--soft"
										onClick={refreshAfterConflict}
									>
										Reintentar actualización de agenda
									</button>
								</div>
							)}
						</div>
					)}

					<div className="pu-grid-2">
						<Campo label="Fecha">
							<SingleDatePickerPill
								value={dateLabel ? dayjs(dateLabel) : dayjs()}
								onChange={(djs) => {
									if (djs && djs.isValid()) {
										setDateLabel(djs.format("YYYY-MM-DD"));
										setSelectedCourtId(null);
										setSubmitError("");
									}
								}}
								minDate={minDate}
								maxDate={maxDate}
								ariaLabel="Fecha"
							/>
						</Campo>
						<Campo label="Horario">
							<OrganizerTimePillSelect
								value={slotStart}
								onChange={(val) => {
									setSlotStart(val);
									setSelectedCourtId(null);
									setSubmitError("");
								}}
								triggerClassName="ef-date-pill-trigger"
								placeholder="--:--"
								singleList
							/>
						</Campo>
					</div>

					<Campo label="Cancha">
						{slotStart && !conflictRefreshBlocked ? (
							<CourtSlotAvailabilityPicker
								sports={sports}
								activeSport={activeSport}
								onSportChange={(s) => {
									setActiveSport(s);
									setSelectedCourtId(null);
									setSubmitError("");
								}}
								durations={durations}
								activeDuration={activeDuration}
								onDurationChange={(d) => {
									setActiveDuration(d);
									setSelectedCourtId(null);
									setSubmitError("");
								}}
								loading={loading}
								loadError={loadError}
								courtOptions={courtOptions}
								selectedCourtId={selectedCourtId}
								onSelectCourt={(courtId) => setSelectedCourtId(courtId)}
								slot={slot}
							/>
						) : (
							<p className="ba-aviso">
								Elegí un horario para ver las canchas disponibles.
							</p>
						)}
					</Campo>

					{selectedOption && (
						<div className="ba-resumen" aria-label="Resumen">
							<span>Duración: {duracionLabel(activeDuration)}</span>
							{price > 0 && <span>Total: {plata(price)}</span>}
						</div>
					)}

					<TurnoFijoBlock
						ctl={turnoFijo}
						date={dateLabel}
						slotStart={slotStart}
						sinProfesional
						faltaTexto="Elegí un horario y una cancha."
					/>

					<Campo label="Nombre">
						<input
							type="text"
							className="ef-input"
							aria-label="Nombre"
							placeholder="Nombre y apellido"
							value={name}
							onChange={(e) => setName(e.target.value)}
						/>
					</Campo>
					<Campo
						label="Teléfono"
						hint={fixed ? "Un turno fijo necesita el teléfono de la persona." : undefined}
					>
						<PhoneCountryInput
							countryCode={phoneCountryCode}
							number={phoneNumber}
							error={phoneTouched && !phoneValid}
							onChange={({ countryCode, number, valid }) => {
								setPhoneCountryCode(countryCode);
								setPhoneNumber(number);
								setPhoneValid(valid);
								setPhoneTouched(true);
								setSubmitError("");
							}}
						/>
						{phoneTouched && !phoneValid && (
							<small style={{ color: "#e53935" }}>Este número no es válido</small>
						)}
					</Campo>
					<Campo label="Notas (opcional)">
						<input
							type="text"
							className="ef-input"
							aria-label="Notas"
							placeholder="Observaciones"
							value={notes}
							onChange={(e) => setNotes(e.target.value)}
						/>
					</Campo>
				</div>
				<div className="zr-side-panel__foot">
					<button
						type="button"
						className="zr-btn zr-btn--ghost"
						onClick={handleHide}
						disabled={submitting}
					>
						Cancelar
					</button>
					<button
						type="button"
						className="zr-btn zr-btn--dark"
						disabled={!canSubmit}
						onClick={handleSubmit}
					>
						{submitting
							? "Creando..."
							: fixed
								? availableCount > 0
									? `Crear ${pluralTurnos(availableCount)}`
									: "Crear turno fijo"
								: "Crear reserva"}
					</button>
				</div>
			</div>
		</div>
	);
}
